import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Layout from "../../Component/Layout";
import { PURCHASE_API } from "../../apiConfig";

/* ==========================================================================
   Bank Statement report — combined Credit (Billing) / Debit (Supplier
   Invoice) ledger, fetched from the Bank Statement backend module.

   GET  ${PURCHASE_API}/bank-statement/filters
       -> { locations: [...], customers: [{partner_code, business_partner_name}],
            suppliers: [...] }

   GET  ${PURCHASE_API}/bank-statement?location=&customer=&supplier=&fromDate=&toDate=
       -> { rows: [...], summary: { totalCredit, totalDebit, balance } }
   ========================================================================== */

const fmtAmount = (val) =>
  val === null || val === undefined
    ? "—"
    : Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (isoStr) => {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/* ---------- Financial year / month helpers (Indian FY: April – March) ---------- */

const pad2 = (n) => String(n).padStart(2, "0");

const MONTH_OPTIONS = [
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
];

// Start year of the financial year that today falls in (Apr 2025 – Mar 2026 -> 2025)
const getCurrentFyStart = () => {
  const now = new Date();
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};

const fyOption = (start) => ({ value: start, label: `${start}-${String(start + 1).slice(-2)}` });

// Returns { fromDate, toDate } (yyyy-mm-dd) for a FY, optionally narrowed to one month
const computeRange = (fyStart, month) => {
  if (!month) {
    return { fromDate: `${fyStart}-04-01`, toDate: `${fyStart + 1}-03-31` };
  }
  const year = month >= 4 ? fyStart : fyStart + 1;
  const lastDay = new Date(year, month, 0).getDate();
  return {
    fromDate: `${year}-${pad2(month)}-01`,
    toDate: `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
};

/* ---------- dd/mm/yyyy date input ----------
   Native <input type="date"> always shows the browser's locale format, so this
   is a text input with a dd/mm/yyyy mask plus a calendar button that opens the
   native picker. The value it reads/emits is still ISO (yyyy-mm-dd). */

const isoToDisplay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

const displayToIso = (text) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  const valid =
    d.getFullYear() === Number(yyyy) && d.getMonth() === Number(mm) - 1 && d.getDate() === Number(dd);
  return valid ? `${yyyy}-${mm}-${dd}` : null;
};

const maskDate = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

function DateInput({ value, onChange }) {
  const [text, setText] = useState(isoToDisplay(value));
  const pickerRef = useRef(null);

  // Keep the text in sync when the value is changed from outside (FY / Month, clear)
  useEffect(() => {
    setText(isoToDisplay(value));
  }, [value]);

  const handleTextChange = (e) => {
    const masked = maskDate(e.target.value);
    setText(masked);
    if (masked === "") {
      onChange("");
      return;
    }
    const iso = displayToIso(masked);
    if (iso) onChange(iso);
  };

  // Drop half-typed / invalid input and go back to the last valid value
  const handleBlur = () => {
    if (text !== "" && !displayToIso(text)) setText(isoToDisplay(value));
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      el.showPicker ? el.showPicker() : el.click();
    } catch (err) {
      el.click();
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        inputMode="numeric"
        className="form-control"
        placeholder="dd/mm/yyyy"
        maxLength={10}
        style={{ height: 32, fontSize: "13px", width: "100%", paddingRight: 30 }}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label="Open calendar"
        style={{
          position: "absolute", right: 4, top: 4, height: 24, width: 24,
          border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "#64748b",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />
    </div>
  );
}

const dropdownOptionStyle = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

const filterCardStyle = { background: "#ffffff", borderRadius: 8, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 18 };
const filterLabelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#334155" };

const thStyle = {
  padding: "12px 14px",
  fontSize: "12px",
  fontWeight: "700",
  color: "#334155",
  textAlign: "left",
  whiteSpace: "nowrap",
  borderBottom: "2px solid #e2e8f0",
  background: "#f1f5f9",
  position: "sticky",
  top: 0,
};

const tdStyle = {
  padding: "11px 14px",
  fontSize: "13px",
  color: "#334155",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #f1f5f9",
};

const INITIAL_FILTERS = {
  location: "",
  customer: "",
  supplier: "",
  fy: "",     // financial year start year, e.g. 2025 for 2025-26
  month: "",  // calendar month number 1-12
  fromDate: "",
  toDate: "",
};

function BankStatement() {
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    customers: [],
    suppliers: [],
    financialYears: [],
  });

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalCredit: 0, totalDebit: 0, balance: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load dropdown options once on mount
  useEffect(() => {
    axios
      .get(`${PURCHASE_API}/bank-statement/filters`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((err) => {
        console.error("BANK STATEMENT FILTERS ERROR:", err);
      });
  }, []);

  const locationOptions = useMemo(
    () => (filterOptions.locations || []).map((loc) => ({ value: loc, label: loc })),
    [filterOptions.locations]
  );

  const customerOptions = useMemo(
    () =>
      (filterOptions.customers || []).map((c) => ({
        value: c.business_partner_name,
        label: c.business_partner_name,
      })),
    [filterOptions.customers]
  );

  const supplierOptions = useMemo(
    () =>
      (filterOptions.suppliers || []).map((s) => ({
        value: s.business_partner_name,
        label: s.business_partner_name,
      })),
    [filterOptions.suppliers]
  );

  // Financial years come from the data itself (earliest to latest transaction)
  const fyOptions = useMemo(
    () => (filterOptions.financialYears || []).map(fyOption),
    [filterOptions.financialYears]
  );

  const fetchStatement = (activeFilters) => {
    setLoading(true);
    setError("");

    const params = {};
    if (activeFilters.location) params.location = activeFilters.location;
    if (activeFilters.customer) params.customer = activeFilters.customer;
    if (activeFilters.supplier) params.supplier = activeFilters.supplier;
    if (activeFilters.fromDate) params.fromDate = activeFilters.fromDate;
    // Send end-of-day so records timestamped during the To Date itself are included
    if (activeFilters.toDate) params.toDate = `${activeFilters.toDate} 23:59:59`;

    axios
      .get(`${PURCHASE_API}/bank-statement`, { params })
      .then((res) => {
        setRows(res.data?.rows || []);
        setSummary(res.data?.summary || { totalCredit: 0, totalDebit: 0, balance: 0 });
      })
      .catch((err) => {
        console.error("BANK STATEMENT FETCH ERROR:", err);
        setError("Failed to load bank statement. Please try again.");
        setRows([]);
        setSummary({ totalCredit: 0, totalDebit: 0, balance: 0 });
      })
      .finally(() => setLoading(false));
  };

  // Auto-fetch whenever any filter changes (debounced), including initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStatement(filters);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== "");
  const handleClearAll = () => setFilters(INITIAL_FILTERS);

  // Financial Year drives From/To. Clearing it also clears Month and the dates.
  const handleFyChange = (opt) => {
    setFilters((prev) => {
      if (!opt) return { ...prev, fy: "", month: "", fromDate: "", toDate: "" };
      return { ...prev, fy: opt.value, ...computeRange(opt.value, prev.month) };
    });
  };

  // Month narrows the selected FY; if no FY is chosen yet, the latest FY in the
  // data is used (falls back to the current FY if the list hasn't loaded).
  const handleMonthChange = (opt) => {
    setFilters((prev) => {
      const fy = prev.fy || (filterOptions.financialYears || [])[0] || getCurrentFyStart();
      const month = opt ? opt.value : "";
      return { ...prev, fy, month, ...computeRange(fy, month) };
    });
  };

  // Typing / picking a custom date overrides FY and Month
  const handleDateChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, fy: "", month: "" }));
  };


  // Derive the currency to display above the table (falls back to INR if rows have none yet)
  const displayCurrency = useMemo(() => {
    const withCurrency = rows.find((r) => r.currency);
    return withCurrency ? withCurrency.currency : "INR";
  }, [rows]);

  return (
    <Layout>
      <div style={{ padding: "30px 40px", backgroundColor: "#f8fafc", minHeight: "calc(100vh - 60px)" }}>

        {/* Page Header */}
        <div style={{ marginBottom: "28px" }}>
          <h2 style={{ margin: "0 0 4px 0", color: "#0f172a", fontWeight: "700", fontSize: "24px" }}>
            Bank Statement
          </h2>
          <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
            Combined view of billing invoices (credit) and supplier invoices (debit)
          </p>
        </div>

        {/* Filter Card */}
        <div style={filterCardStyle}>
          <div style={{ overflowX: "auto", overflowY: "hidden", paddingBottom: 2 }}>
          <div
            style={{
              display: "grid",
              gap: "16px",
              alignItems: "end",
              gridTemplateColumns: "repeat(7, minmax(130px, 1fr)) auto",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <label style={filterLabelStyle}>Location</label>
              <Select
                options={locationOptions}
                value={locationOptions.find((o) => o.value === filters.location) || null}
                onChange={(opt) => handleChange("location", opt ? opt.value : "")}
                placeholder="Select"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={filterLabelStyle}>Customer</label>
              <Select
                options={customerOptions}
                value={customerOptions.find((o) => o.value === filters.customer) || null}
                onChange={(opt) => handleChange("customer", opt ? opt.value : "")}
                placeholder="Select"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={filterLabelStyle}>Supplier</label>
              <Select
                options={supplierOptions}
                value={supplierOptions.find((o) => o.value === filters.supplier) || null}
                onChange={(opt) => handleChange("supplier", opt ? opt.value : "")}
                placeholder="Select"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={filterLabelStyle}>Financial Year</label>
              <Select
                options={fyOptions}
                value={fyOptions.find((o) => o.value === filters.fy) || null}
                onChange={handleFyChange}
                placeholder="Select"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={filterLabelStyle}>Month</label>
              <Select
                options={MONTH_OPTIONS}
                value={MONTH_OPTIONS.find((o) => o.value === filters.month) || null}
                onChange={handleMonthChange}
                placeholder="Select"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={filterLabelStyle}>From Date</label>
              <DateInput value={filters.fromDate} onChange={(v) => handleDateChange("fromDate", v)} />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={filterLabelStyle}>To Date</label>
              <DateInput value={filters.toDate} onChange={(v) => handleDateChange("toDate", v)} />
            </div>
            <div>
              <button
                type="button"
                onClick={handleClearAll}
                disabled={!hasActiveFilters}
                style={{
                  height: 32, padding: "0 14px", fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap",
                  border: "1px solid #cbd5e1", borderRadius: 6, background: "#ffffff", color: "#334155",
                  cursor: hasActiveFilters ? "pointer" : "not-allowed", opacity: hasActiveFilters ? 1 : 0.5,
                }}
              >
                Clear All
              </button>
            </div>
          </div>
          </div>
        </div>

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
            borderRadius: "10px", padding: "12px 16px", marginBottom: "18px", fontSize: "13px"
          }}>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "20px", marginBottom: "24px"
        }}>
          <div style={{
            background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
            border: "1px solid #a7f3d0", borderRadius: "18px", padding: "28px 30px",
            boxShadow: "0 4px 10px -2px rgba(16,185,129,0.15)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#047857", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Credit
            </div>
            <div style={{ fontSize: "34px", fontWeight: "800", color: "#065f46", marginTop: "8px" }}>
              ₹ {fmtAmount(summary.totalCredit)}
            </div>
          </div>
          <div style={{
            background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
            border: "1px solid #fecaca", borderRadius: "18px", padding: "28px 30px",
            boxShadow: "0 4px 10px -2px rgba(239,68,68,0.15)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#b91c1c", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Debit
            </div>
            <div style={{ fontSize: "34px", fontWeight: "800", color: "#991b1b", marginTop: "8px" }}>
              ₹ {fmtAmount(summary.totalDebit)}
            </div>
          </div>
          <div style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
            border: "1px solid #bfdbfe", borderRadius: "18px", padding: "28px 30px",
            boxShadow: "0 4px 10px -2px rgba(59,130,246,0.15)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Net Balance
            </div>
            <div style={{ fontSize: "34px", fontWeight: "800", color: "#1e40af", marginTop: "8px" }}>
              ₹ {fmtAmount(summary.balance)}
            </div>
          </div>
        </div>

        {/* Statement Table */}
        <div style={{
          background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", overflow: "hidden"
        }}>
          {/* Currency indicator — right aligned above table */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px",
            padding: "14px 18px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc"
          }}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Currency
            </span>
            <span style={{
              fontSize: "12px", fontWeight: "700", color: "#334155",
              background: "#e2e8f0", borderRadius: "6px", padding: "3px 10px"
            }}>
              {displayCurrency}
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Customer / Supplier Name</th>
                  <th style={thStyle}>PO Number / Invoice</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#b91c1c" }}>Debit</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#047857" }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                      Loading transactions...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                      No transactions found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const isCredit = row.type === "credit";
                    const isGlPosting = row.source === "gl_posting";
                    const partyName = isGlPosting
                      ? row.glDescription
                      : isCredit
                      ? row.customerName
                      : row.supplierName;
                    // Credit (billing): show PO Number. Debit (GRN/supplier invoice): show GRN Number.
                    // GL posting rows: show the GL code instead.
                    const reference = isGlPosting
                      ? row.glCode
                      : isCredit
                      ? row.customerPO
                      : row.grnNumber;

                    return (
                      <tr
                        key={`${row.type}-${row.billingInvoice || row.supplierInvoice || idx}-${idx}`}
                        style={{ background: isCredit ? "#f0fdf4" : "#fef7f7" }}
                      >
                        <td style={{ ...tdStyle, fontWeight: "600" }}>{fmtDate(row.date)}</td>
                        <td style={tdStyle}>{partyName || ""}</td>
                        <td style={tdStyle}>{reference || ""}</td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: "600", color: "#b91c1c" }}>
                          {!isCredit && row.debit !== null && row.debit !== undefined ? fmtAmount(row.debit) : ""}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: "600", color: "#047857" }}>
                          {isCredit && row.credit !== null && row.credit !== undefined ? fmtAmount(row.credit) : ""}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default BankStatement;